import { Link, PageLink } from '@ipfabric/ui-components/Link/Link';
import type { CreateLinkPropsFunction, FilterValue } from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import { identity, pipe } from 'fp-ts/function';
import type { ReactNode } from 'react';

export const maybeWrapWithLink: <TRow>(
  row: TRow,
  contrast: boolean,
  createLinkProps?: CreateLinkPropsFunction<TRow>,
  wrapWithLink?: boolean,
) => (node: ReactNode, currentValue?: FilterValue) => ReactNode = (
  row,
  contrast,
  createLinkProps,
  wrapWithLink,
) =>
  createLinkProps && wrapWithLink !== false
    ? (node: ReactNode, currentValue?: FilterValue) => {
        const linkProps = createLinkProps(row, currentValue);
        if (!linkProps) {
          return node;
        }
        // `hideIcon` is presentation, not a router prop — strip it here so it
        // never reaches the anchor. `PageLink` is `Link` with the trailing glyph
        // baked in and `icon` omitted from its props, so opting out means
        // rendering the same underlying `Link` without it.
        const { hideIcon, ...routerProps } = linkProps;
        const accent = contrast ? 'contrast' : 'positive';

        return hideIcon ? (
          <Link renderAs='link' accent={accent} {...routerProps}>
            {node}
          </Link>
        ) : (
          <PageLink accent={accent} {...routerProps}>
            {node}
          </PageLink>
        );
      }
    : identity;

export const maybeWrapNonEmptyWithLink =
  <TRow,>(
    row: TRow,
    contrast: boolean,
    createLinkProps?: CreateLinkPropsFunction<TRow>,
    wrapWithLink?: boolean,
  ): ((node: ReactNode, currentValue?: FilterValue) => ReactNode) =>
  (node: ReactNode, currentValue?: FilterValue) =>
    pipe(
      node,
      nullable.fromEmptyString,
      nullable.chain((n) =>
        maybeWrapWithLink(
          row,
          contrast,
          createLinkProps,
          wrapWithLink,
        )(n, currentValue),
      ),
    );
